import React from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Filter } from 'lucide-react';
import FindingSeverityBadge from '../findings/FindingSeverityBadge';
import { getFindingTitle, isDerivedFindingTitle } from '../../utils/findingUtils';
import { useApplicationFindings } from '../../hooks/useApplicationFindings';
import { Spinner } from '../../design-system';

// Human-readable labels, mirroring the Findings list page filter options.
const STATUS_LABELS: Record<string, string> = {
  actionable: 'Actionable',
  all: 'All Statuses',
  open: 'Open',
  needs_manual_review: 'Needs Review',
  resolved: 'Resolved',
  ignored: 'Ignored',
  false_positive: 'False Positive',
};

const SCAN_TYPE_LABELS: Record<string, string> = {
  semgrep_scan: 'Security Scan',
  secrets_scan: 'Secrets Scan',
  deep_ai_scan: 'Deep AI Security Scan',
  sbom_scan: 'SBOM Scan',
};

/**
 * Sidebar shown while viewing a single finding. Replaces the application sidebar with the
 * list of findings the user came from (same filters/order), so they can page through them.
 */
const FindingsListSidebar: React.FC = () => {
  const { applicationId, findingId } = useParams<{ applicationId: string; findingId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Mirror the Findings list filters (carried in the URL); status defaults to 'actionable'.
  const severity = searchParams.get('severity');
  const status = searchParams.get('status') ?? 'actionable';
  const scanType = searchParams.get('scan_type');
  const scanBatchIds = searchParams.getAll('scan_batch_id');

  const { findings, isLoading, error } = useApplicationFindings(applicationId, {
    severity,
    status,
    scanType,
    scanBatchIds,
  });

  // Preserve the active filters when navigating between findings.
  const search = location.search;

  // Applied filters shown as chips so the user knows which list they're paging through.
  const appliedFilters = [
    STATUS_LABELS[status] ?? status,
    ...(severity ? [severity.charAt(0).toUpperCase() + severity.slice(1)] : []),
    ...(scanType ? [SCAN_TYPE_LABELS[scanType] ?? scanType] : []),
    ...(scanBatchIds.length === 0
      ? []
      : [scanBatchIds.length === 1 ? '1 scan' : `${scanBatchIds.length} scans`]),
  ];

  return (
    <aside className="w-64 h-full bg-ui-background-primary border-r border-ui-border-primary flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-ui-border-primary">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-ui-text-primary">Findings</h2>
          {!isLoading && !error && (
            <span className="text-xs text-ui-text-tertiary">{findings.length}</span>
          )}
        </div>
        {/* Applied filters */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Filter className="w-3 h-3 text-ui-text-tertiary flex-shrink-0" />
          {appliedFilters.map((label, i) => (
            <span
              key={`${label}-${i}`}
              className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-ui-background-tertiary text-ui-text-secondary"
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Findings list */}
      <nav className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : error ? (
          <div className="px-2 py-4 text-sm text-red-500">{error}</div>
        ) : findings.length === 0 ? (
          <div className="px-2 py-4 text-sm text-ui-text-tertiary">No findings</div>
        ) : (
          <div className="space-y-1">
            {findings.map((finding) => {
              const active = finding.id === findingId;
              return (
                <Link
                  key={finding.id}
                  to={`/repositories/${applicationId}/findings/${finding.id}${search}`}
                  aria-current={active ? 'true' : undefined}
                  className={`block px-2 py-2 rounded-lg transition-colors ${
                    active
                      ? 'bg-brand-primary/20 dark:bg-brand-primaryDarkMode/20'
                      : 'hover:bg-ui-background-tertiary'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FindingSeverityBadge severity={finding.severity} showLabel={false} />
                    <span
                      className={`text-sm truncate ${isDerivedFindingTitle(finding) ? 'capitalize ' : ''}${
                        active
                          ? 'text-ui-text-primary font-medium'
                          : 'text-ui-text-secondary'
                      }`}
                    >
                      {getFindingTitle(finding)}
                    </span>
                  </div>
                  {finding.file_path && (
                    <p className="mt-1 text-xs text-ui-text-tertiary truncate">
                      {finding.file_path}
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default FindingsListSidebar;
